import { ListboxOptions } from '@headlessui/react'
import classNames from 'classnames'
import isEmpty from 'ramda/src/isEmpty'
import toLower from 'ramda/src/toLower'
import React, { KeyboardEventHandler, useState } from 'react'
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll'
import DropdownListCreateOption from '../../Dropdown/List/DropdownListCreateOption'
import DropdownListNoDataOption from '../../Dropdown/List/DropdownListNoDataOption'
import SearchBox from '../../SearchBox'
import type { Placement } from '../../Tooltip'
import type { IMultiselectProps } from '../Multiselect'
import MultiselectOption from './MultiselectOption'

export interface IMultiselectListProps extends Partial<IMultiselectProps> {
  popperStyles?: { [key: string]: React.CSSProperties }
  popperAttributes?: { [key: string]: { [key: string]: string } | undefined }
  popperPlacement?: Placement | null
  setPopperElement?: React.Ref<HTMLDivElement>
  onSearchChange?: (term: string) => void
  searchValue?: string
  loadMore?: () => void
  hasNextPage?: boolean
}

const initialFilterState = ''

const MultiselectList = ({
  data,
  loading,
  valueField,
  textField,
  value,
  renderItem,
  allowCreate,
  isItemRemovable,
  popperPlacement,
  setPopperElement,
  popperStyles,
  popperAttributes,
  onSearchChange,
  searchValue,
  loadMore,
  hasNextPage
}: IMultiselectListProps) => {
  const [filter, setFilter] = useState(initialFilterState)

  const handleSearchChange = (value: string) => {
    setFilter(value)
    onSearchChange?.(value)
  }

  const observerRef = useInfiniteScroll({ loadMore, hasNextPage })

  if (loading && (!data || isEmpty(data)) && !searchValue) {
    return null
  }

  const onKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.key === 'Enter') {
      setFilter('')
      onSearchChange?.('')
    }
  }

  return (
    <ListboxOptions
      transition
      ref={setPopperElement}
      onKeyDown={onKeyDown}
      style={popperStyles?.popper}
      {...popperAttributes?.popper}
      className={classNames([
        'absolute z-20 flex flex-col py-1 w-full max-h-[250px] bg-white border border-oxford-gray-100 shadow-lg origin-top transition duration-200 ease-out data-[closed]:scale-95 data-[closed]:opacity-0',
        {
          'rounded-b-md': popperPlacement && popperPlacement !== 'top',
          'rounded-t-md': popperPlacement && popperPlacement === 'top'
        }
      ])}
    >
      <React.Fragment>
        <SearchBox
          value={searchValue ?? filter}
          onChange={handleSearchChange}
        />
        <div className="flex flex-col overflow-auto">
          {data && !isEmpty(data) ? (
            data.map((dataItem) => {
              if (
                [dataItem?.isActive, dataItem?.active].some(
                  (value) => typeof value === 'boolean' && !value
                )
              ) {
                return null
              }

              return (
                <MultiselectOption
                  key={dataItem[valueField!]}
                  dataItem={dataItem}
                  textField={textField}
                  valueField={valueField}
                  value={value}
                  renderItem={renderItem}
                  disabled={!isItemRemovable?.({ value, item: dataItem, data })}
                  onClick={() => {
                    setFilter(initialFilterState)
                    onSearchChange?.('')
                  }}
                />
              )
            })
          ) : (
            <DropdownListNoDataOption />
          )}

          {allowCreate &&
            !!filter.trim().length &&
            !data?.find(
              (dataItem) =>
                toLower(dataItem[textField!]) === toLower(filter.trim())
            ) && (
              <DropdownListCreateOption
                filter={filter}
                textField={textField!}
                setFilter={setFilter}
              />
            )}

          {hasNextPage && <div ref={observerRef} className="h-px" />}
        </div>
      </React.Fragment>
    </ListboxOptions>
  )
}

export default MultiselectList
